import { useState } from 'react';
import { useParams } from 'react-router-dom';

import Button from '@liquidweb-dev/undercurrent/Button';
import Divider from '@liquidweb-dev/undercurrent/Divider';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import ArrowUpRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowUpRight';
import EllipsisIcon from '@liquidweb-dev/undercurrent/icons/lucide/Ellipsis';
import TrashIcon from '@liquidweb-dev/undercurrent/icons/lucide/Trash2';
import ListItemIcon from '@liquidweb-dev/undercurrent/ListItemIcon';
import Menu from '@liquidweb-dev/undercurrent/Menu';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import DialogDestroyServer from 'components/DialogDestroyServer';
import DialogNutanixPrism from 'components/DialogNutanixPrism';
import ActionsContainer from 'components/HostingDetailsActions/ActionsContainer';
import ActionsLoader from 'components/HostingDetailsActions/ActionsLoader';
import FavoriteMenuItem from 'components/HostingDetailsActions/FavoriteMenuItem';
import useAssetDetails, {
	type AssetDetails,
} from 'modules/queries/asset/useDetails';
import hostingDetailsAlsowith from 'pages/servers/details/hostingDetailsAlsowith';

const SArrowUpRight = () => (
	<ArrowUpRightIcon
		sx={(theme) => ({ height: theme.size.xS, width: theme.size.xS })}
	/>
);

const ActionsContent = ({
	domain,
	status,
	uniq_id: uniqId,
	scheduledCancellationDate,
}: AssetDetails) => {
	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(
		null,
	);
	const [isPrismDialogOpen, setIsPrismDialogOpen] = useState(false);
	const [isDestroyDialogOpen, setIsDestroyDialogOpen] = useState(false);

	const isActive = status === 'Active';
	const isCancellationScheduled =
		(scheduledCancellationDate?.DateTime || '').length > 0;
	const showDestroyServer = isActive && !isCancellationScheduled;

	return (
		<ActionsContainer>
			<Button
				variant="primary"
				size="large"
				onClick={() => setIsPrismDialogOpen(true)}
				sx={{ width: { xs: '100%', md: 'auto' } }}
				startIcon={<SArrowUpRight />}
			>
				Prism Central
			</Button>
			<IconButton
				variant="secondary"
				size="large"
				onClick={(event) => setMenuAnchorEl(event.currentTarget)}
				sx={{ flexShrink: 0 }}
			>
				<EllipsisIcon />
			</IconButton>

			<Menu
				open={Boolean(menuAnchorEl)}
				onClose={() => setMenuAnchorEl(null)}
				anchorEl={menuAnchorEl}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			>
				<FavoriteMenuItem
					onClick={() => setMenuAnchorEl(null)}
					favoritesDisplayName={domain}
					uniqId={uniqId}
				/>

				{showDestroyServer && <Divider sx={{ marginX: { sm: -50 } }} />}

				{showDestroyServer && (
					<MenuItem
						onClick={() => {
							setMenuAnchorEl(null);
							setIsDestroyDialogOpen(true);
						}}
						sx={{
							':hover, :focus-visible': {
								backgroundColor: ({ vars }) => vars.palette.uc.danger.bg.main,
							},
						}}
					>
						<ListItemIcon>
							<TrashIcon
								sx={({ vars }) => ({ color: vars.palette.uc.danger.icon.main })}
							/>
						</ListItemIcon>
						<Typography
							variant="label-lg"
							sx={{ color: 'uc.danger.icon.strong' }}
						>
							Destroy Server
						</Typography>
					</MenuItem>
				)}
			</Menu>

			<DialogNutanixPrism
				open={isPrismDialogOpen}
				onClose={() => setIsPrismDialogOpen(false)}
				uniqId={uniqId}
			/>
			<DialogDestroyServer
				open={isDestroyDialogOpen}
				onClose={() => setIsDestroyDialogOpen(false)}
				uniqId={uniqId}
			/>
		</ActionsContainer>
	);
};

/**
 * Actions for the Nutanix detail page: Prism Central as the primary control,
 * with the asset's remaining actions in the overflow menu.
 */
const Actions = () => {
	const { uniqId } = useParams<{ uniqId: string }>();
	const { data, isLoading, isError } = useAssetDetails(
		{
			alsowith: hostingDetailsAlsowith,
			uniq_id: uniqId,
		},
		{ enabled: Boolean(uniqId) },
	);

	if (isLoading) {
		return <ActionsLoader />;
	}

	if (isError || !data) {
		return null;
	}

	return <ActionsContent {...data} />;
};

export default Actions;
